import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { serviceFaqs } from "@/data/serviceFaqs";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import img from "@/assets/hidden-audio.jpg";

const bullets = [
  "Premium invisible-series in-wall and in-ceiling speakers",
  "Speakers fully concealed behind drywall, plaster, or wallpaper",
  "Zero visible grilles — paintable, paperable, plasterable",
  "Custom subwoofer concealment in cabinetry or floors",
  "Outdoor invisible speakers for landscape and patio",
  "Tuned room-by-room for reference-grade clarity",
];

export const Route = createFileRoute("/services/hidden-audio")({
  head: () => ({
    meta: [
      { title: "Invisible & Hidden Speaker Installation | Apex AV Denver" },
      { name: "description", content: "Invisible series speakers plastered behind your walls. Reference-grade audio with zero visible hardware in Denver homes." },
      { name: "robots", content: "index, follow" },
      { name: "keywords", content: "hidden audio Denver, invisible speaker installation Denver, plaster-in speakers Denver, concealed speaker installation Denver, architectural audio Denver, designer speaker installation Denver, in-wall speakers Denver, in-ceiling speakers Denver, luxury home audio Denver, invisible surround sound Denver" },
      { property: "og:title", content: "Invisible Speakers — Apex AV Denver" },
      { property: "og:description", content: "Hear it. Don't see it. Plastered-in invisible speakers across Denver." },
      { property: "og:image", content: abs(img) },
      { name: "twitter:image", content: abs(img) },
      { property: "og:url", content: "https://apexav.com/services/hidden-audio" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Invisible Speakers — Apex AV Denver" },
      { name: "twitter:description", content: "Hear it. Don't see it. Plastered-in invisible speakers across Denver." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/services/hidden-audio" }, { rel: "preload", as: "image", href: img, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "hidden-audio",
            name: "Hidden & Invisible Audio",
            description: "Premium invisible-series speakers plastered behind drywall in Denver homes.",
            pageTitle: "Invisible & Hidden Speaker Installation | Apex AV Denver",
            pageDescription: "Invisible series speakers plastered behind your walls. Reference-grade audio with zero visible hardware in Denver homes.",
            image: img,
            faqs: serviceFaqs["hidden-audio"],
            serviceType: "Hidden & Invisible Audio",
            breadcrumbName: "Hidden & Invisible Audio Denver",
            offerCatalog: bullets,
          })
        ),
      },
    ],
  }),
  component: () => (
    <ServiceDetail
      faqs={serviceFaqs["hidden-audio"]}
      topics={["whole-home-audio", "home-theater", "smart-home"]}
      eyebrow="Hidden audio"
      title="Hear it. Don't see it."
      intro={<>Invisible-series speakers install behind drywall and finish flush with your wall — <strong>hidden audio in Denver</strong> with <strong>invisible speaker installation</strong> that is invisible to the eye, indistinguishable to the ear.</>}
      image={img}
      imageAlt="Invisible in-wall and in-ceiling speaker installation in a Cherry Creek Denver home"
      brands={["episode", "klipsch", "sonos"]}
      bullets={bullets}
      features={[
        { title: "Designer-approved", desc: "Architects and interior designers love that nothing breaks the wall plane." },
        { title: "Reference sound", desc: "Modern invisible drivers rival conventional in-wall performance." },
        { title: "Anywhere", desc: "Bathrooms, hallways, ceilings, even outdoor pergolas — fully concealed." },
      ]}
    />
  ),
});
